/**
 * REST API bearer: prefer StepFlow session JWT (per-workspace); fall back to Cognito id token.
 */
const SF_ACCESS = 'stepflowAccessToken';

export function getRestApiBearerToken() {
  const real =
    localStorage.getItem(SF_ACCESS) ||
    localStorage.getItem('idToken') ||
    '';
  // LOCAL DEV (doc/dev/LOCAL_DEV_STACK.md): with the dev-auth bypass on, there's no Cognito session,
  // so return a non-empty stub so services gated on ``if (!token) throw 'Not authenticated'`` proceed.
  // The local dev-server ignores the token (it resolves the tenant server-side). Prod-safe: the flag
  // is only ever set in a gitignored .env.local by dev.sh.
  if (!real && import.meta.env.VITE_LOCAL_DEV_AUTH === '1') return 'dev-local-token';
  return real;
}

export function setStepflowAccessToken(token) {
  if (token) {
    localStorage.setItem(SF_ACCESS, token);
  } else {
    localStorage.removeItem(SF_ACCESS);
  }
}

export function clearStepflowRestSession() {
  localStorage.removeItem(SF_ACCESS);
  localStorage.removeItem('stepflowActiveTenantId');
}

/**
 * Agent AppSync channel scope segment — must match ``stepflow_core.events.scope_segment``
 * and the Events authorizer (org tenant when present, else ``user-{sub}``).
 *
 * Prefer this over UI-only ``activeTenantId`` when subscribing: the authorizer derives
 * expected scope from the **same** bearer token; a mismatch yields subscribe_success but
 * **no** ``data`` frames while Brain publishes on the token's tenant.
 */
export function appSyncAgentScopeFromBearerToken(token) {
  const t = String(token || '').trim();
  if (!t) return undefined;
  const parts = t.split('.');
  if (parts.length < 2) return undefined;
  try {
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const payload = JSON.parse(atob(padded));
    // StepFlow session JWT carries ``tid`` in the payload (typ lives on the JWT
    // header, not the payload — do not gate on it). Cognito ID tokens carry
    // ``custom:tenant_id``. Either is the workspace tenant — prefer it over the
    // personal ``user-{sub}`` fallback which the backend authorizer will reject
    // for any user that *does* have a workspace.
    const sf = String(payload.tid || '').trim();
    if (sf) return sf;
    const ct = String(
      payload['custom:tenant_id'] || payload.custom_tenant_id || '',
    ).trim();
    if (ct) return ct;
    const sub = String(payload.sub || '').trim();
    return sub ? `user-${sub}` : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Workspace tenant id from the bearer token — the ``tid`` (session JWT) or
 * ``custom:tenant_id`` (Cognito) claim, with NO ``user-{sub}`` fallback.
 *
 * Used to subscribe to the tenant Context channel (``default/context/{tid}``).
 * Unlike ``appSyncAgentScopeFromBearerToken`` there is no personal-scope
 * fallback: the Events authorizer's ``context`` branch requires an exact
 * ``tid`` match, so a personal user (no workspace) has no context channel and
 * we return ``undefined`` (the hook then no-ops).
 */
export function tenantIdFromBearerToken(token) {
  const t = String(token || '').trim();
  if (!t) return undefined;
  const parts = t.split('.');
  if (parts.length < 2) return undefined;
  try {
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const payload = JSON.parse(atob(padded));
    const sf = String(payload.tid || '').trim();
    if (sf) return sf;
    const ct = String(
      payload['custom:tenant_id'] || payload.custom_tenant_id || '',
    ).trim();
    return ct || undefined;
  } catch {
    return undefined;
  }
}
